import {
  ArrowHeadRightIcon,
  GroupedGroupOption,
  GroupOption,
  GroupPicker,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AscUnresolvedEntity } from '../../../utils/timetable/timetableVersion';
import { ASC_MAPPING_SKIP, suggestAscMatch } from '../utils/ascMapping';
import styles from './AscImportMappingRow.module.scss';

/** Referenzstabile Leerlisten — sonst würde ein frisches `[]` je Render die Memoisierung aushebeln. */
const EMPTY_OPTIONS: SelectOptionType[] = [];
const EMPTY_GROUPED: GroupedGroupOption[] = [];

type AscImportMappingRowProps = {
  entry: AscUnresolvedEntity;
  /**
   * Auswahlliste für Nicht-Gruppen. MUSS referenzstabil sein — sie wird einmal im Elternteil
   * gebaut. Ein neues Array je Render würde react-select seinen Zustand neu aufbauen lassen.
   */
  options?: SelectOptionType[];
  /**
   * Nur Gruppen (`grouped`): die nach Teilung verschachtelten Optionen für den `GroupPicker`. Ebenfalls
   * referenzstabil (s. `ascGroupOptions`).
   */
  groupedOptions?: GroupedGroupOption[];
  /**
   * Nur bei Gruppen: die Klasse, zu der die Gruppe gehört. Ohne sie ist ein Gruppenname wie „A"
   * oder „1" nicht zuzuordnen — dieselben Namen kommen in jeder Klasse vor.
   */
  classLabel?: string;
  /**
   * Nur bei Gruppen: die Teilung, auf die die Auswahl begrenzt ist. Ohne den Hinweis wäre eine kurze
   * Liste nicht zu erklären — die Gruppen einer aSc-Teilung müssen alle in dieselbe Teilung.
   */
  divisionName?: string;
  /** Gruppen-Zeile: `GroupPicker` (Single-Select) statt `Select`, Optionen aus `groupedOptions`. */
  grouped?: boolean;
  /** `undefined` = noch nichts gewählt, `null` = überspringen, sonst die Ziel-UUID. */
  value: string | null | undefined;
  onChange: (entry: AscUnresolvedEntity, targetUuid: string | null | undefined) => void;
  disabled?: boolean;
};

/** Die gewählte Gruppe im verschachtelten Optionsbaum finden (für den `value` des `GroupPicker`). */
function findGroupOption(groups: GroupedGroupOption[], value: string): GroupOption | undefined {
  for (const group of groups) {
    for (const option of group.options) {
      if (option.value === value) return option;
    }
  }
  return undefined;
}

/**
 * Eine Zeile der manuellen Zuordnung: „so heißt es in der Datei" → Auswahl bei uns.
 *
 * Bewusst `memo` mit stabilen Props: bei einer kompletten Schule stehen hier schnell 50+ Zeilen,
 * und ohne Memoisierung würde jede einzelne Auswahl alle Zeilen samt Optionslisten neu rendern.
 */
export const AscImportMappingRow = memo(
  ({
    entry,
    options,
    groupedOptions,
    classLabel,
    divisionName,
    grouped = false,
    value,
    onChange,
    disabled = false,
  }: AscImportMappingRowProps) => {
    const { t } = useTranslation();
    const flatOptions = options ?? EMPTY_OPTIONS;

    // Fuzzy-Treffer nur für Nicht-Gruppen: aSc schreibt „Raum 1 - EG", bei uns heißt derselbe Raum
    // „Raum 1 (EG)". Gruppen matcht das Backend ohnehin per Namen; die offenen Fälle sind echt unklar,
    // deshalb kein Vorschlag. Nur von [entry, options] abhängig — die Optionslisten sind referenzstabil.
    const suggestion = useMemo(
      () => (grouped ? null : suggestAscMatch(entry, flatOptions)),
      [grouped, entry, flatOptions],
    );

    // Einmalig vorbefüllen. `options` trifft erst nach dem Laden der Stammdaten ein, deshalb am
    // Vorschlag festgemacht statt am Mount — und per Ref genau einmal, sonst käme der Vorschlag
    // nach jedem Leeren des Selects sofort zurück und der Anwender käme ihn nicht los.
    const suggestionApplied = useRef(false);
    useEffect(() => {
      if (grouped || suggestionApplied.current || !suggestion) return;
      suggestionApplied.current = true;
      if (value === undefined) {
        onChange(entry, suggestion.value);
      }
    }, [grouped, suggestion, value, entry, onChange]);

    const selected: SelectOptionType | undefined =
      value === null
        ? { value: ASC_MAPPING_SKIP, label: t('pinboard.actions.ascImportMapping.skip') }
        : flatOptions.find((option) => option.value === value);

    // Gruppen-Zeile: der `GroupPicker`-Wert ist ein `GroupOption` (oder `null` = nichts gewählt).
    const selectedGroup: GroupOption | null =
      value === null
        ? { value: ASC_MAPPING_SKIP, label: t('pinboard.actions.ascImportMapping.skip') }
        : value === undefined
          ? null
          : (findGroupOption(groupedOptions ?? EMPTY_GROUPED, value) ?? null);

    const isSuggested = !grouped && suggestion !== null && value === suggestion.value;

    return (
      <div className={styles['mapping-row']}>
        <div className={styles['mapping-label']}>
          <span className={styles['mapping-name']}>
            {entry.name ?? entry.label}
            {classLabel && <span className={styles['mapping-class']}>{classLabel}</span>}
          </span>
          {entry.reason === 'AMBIGUOUS' && (
            <span className={styles['mapping-hint']}>{t('pinboard.actions.ascImportReason.AMBIGUOUS')}</span>
          )}
          {divisionName && (
            <span className={styles['mapping-hint']}>
              {t('pinboard.actions.ascImportMapping.division', { division: divisionName })}
            </span>
          )}
          {isSuggested && (
            <span className={styles['mapping-suggestion']}>
              {t('pinboard.actions.ascImportMapping.suggestion', { score: Math.round(suggestion.score * 100) })}
            </span>
          )}
        </div>
        <ArrowHeadRightIcon className={styles['mapping-arrow']} />
        <div className={styles['mapping-select']}>
          {grouped ? (
            <GroupPicker
              dense
              isMulti={false}
              isClearable
              dimUnmatched
              fitMenuToOptions
              disabled={disabled}
              name={`asc-mapping-${entry.kind}-${entry.ascId}`}
              placeholder={t('pinboard.actions.ascImportMapping.placeholder')}
              options={groupedOptions ?? EMPTY_GROUPED}
              value={selectedGroup}
              onChange={(option) => {
                if (!option) {
                  onChange(entry, undefined);
                  return;
                }
                onChange(entry, option.value === ASC_MAPPING_SKIP ? null : option.value);
              }}
            />
          ) : (
            <Select
              dense
              isClearable
              isSearchable
              menuPosition='fixed'
              disabled={disabled}
              name={`asc-mapping-${entry.kind}-${entry.ascId}`}
              placeholder={t('pinboard.actions.ascImportMapping.placeholder')}
              options={flatOptions}
              value={selected}
              onChange={(event) => {
                const option = event as SelectOptionType | null;
                if (!option) {
                  onChange(entry, undefined);
                  return;
                }
                onChange(entry, option.value === ASC_MAPPING_SKIP ? null : (option.value as string));
              }}
            />
          )}
        </div>
      </div>
    );
  },
);

AscImportMappingRow.displayName = 'AscImportMappingRow';
